// Considered: AspectDefSummaryMolecule — rejected because that molecule is the COLLAPSED row
// (glyph, name, provenance strip, needs chips); this is the expanded per-attachment editor —
// attribution, *on this type*, Needs, Options, Arc wiring, Targets, Version and the apply /
// detach footer — 75 contract datums and 36 states of its own. The summary stays as its
// header. `Source: studio/type-attachment.jsx` (`AttachmentEditor` + `DefSummary`), with the
// `c-sec` / `c-row` / `CSeg` / `CSelect` / `CNum` primitives from `studio/aspect-collection.jsx`
// mapped onto SegmentedControlAtom / DropdownAtom / InputAtom / ButtonAtom.
// See spec `### Design System Reuse Audit` → New components — justified.
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import './AspectAttachmentSheetMolecule.scss';

/** How a held field gets its value (prototype `FILL_WORD`). */
export type AttachmentFillWord = 'asked' | 'derived' | 'sibling' | 'counted' | 'rollup';
/** What supplies the aspect's value (prototype `KIND_WORD`). */
export type AttachmentCollectionKind =
  | 'manual'
  | 'query'
  | 'measure'
  | 'integration'
  | 'orbit'
  | 'set'
  | 'timeframe'
  /**
   * The platform's own `AspectCollectionKind` (types/aspect.d.ts) is a shorter list than the
   * prototype's, and two of its members have no prototype word: an aspect an arc collects, and
   * one that exists to be judged. A real attachment carries one of these, so the sheet has to
   * be able to name it rather than report every such aspect as `manual`.
   */
  | 'arc'
  | 'evaluated';
/** How the aspect's truth relates to time (prototype `d.truth.temporal`). */
export type AttachmentTemporal = 'timeless' | 'effective_interval' | 'point';
/** The attachment's lifecycle state on this type (prototype `ATTACH_STATES`). */
export type AttachmentStartState = 'active' | 'observing' | 'paused';
/** What an aspect does inside one arc stage (prototype `STAGE_ROLES`). */
export type AttachmentStageRole = 'none' | 'collect' | 'required' | 'seed';
/** Where a need's answer is recorded (prototype `NEED_ANSWERERS`). */
export type AttachmentNeedAt = 'type' | 'record';

const START_STATES: AttachmentStartState[] = ['active', 'observing', 'paused'];
const STAGE_ROLES: { id: AttachmentStageRole; asks?: boolean }[] = [
  { id: 'none' },
  { id: 'collect', asks: true },
  { id: 'required' },
  { id: 'seed', asks: true },
];
const TARGET_MODES = ['defer', 'seed', 'default'] as const;
const PIN_MODES = ['float', 'pin'] as const;
const NEED_ANSWERERS: AttachmentNeedAt[] = ['type', 'record'];

/**
 * The aspect's own sealed definition, as this type may read it. `null` means the
 * definition does not resolve in this workspace's registry — which is NOT the same
 * as "declares nothing", and the sheet says so rather than reporting itself settled.
 */
export interface AttachmentDefinition {
  kind: AttachmentCollectionKind;
  holds: { name: string; type: string; fill: AttachmentFillWord; rules?: string }[];
  /** The definition carries judgement bands — it is measured against a target. */
  evaluated: boolean;
  bands: { status: string; pct: number }[];
  temporal: AttachmentTemporal;
  /** Set only when the definition revalidates; absent means values never go stale. */
  expiryDays?: number;
  /** It holds fields but nobody is asked for any of them — no stage can raise a check-in. */
  computed: boolean;
  /** A `manual` aspect collected ad-hoc: there is no schedule, so no cadence to answer. */
  adhoc?: boolean;
}

/**
 * The need keys whose question and noun the prototype names (`NEED_KINDS`). A
 * declared key outside this set has no authored copy, so it reads as itself
 * rather than as a missing-translation path.
 */
const NAMED_NEED_KEYS = ['cadence', 'arc', 'perspective', 'recordSet', 'anchor'];

export const needCopy = (key: string, part: 'ask' | 'noun', t: (key: string) => string): string =>
  NAMED_NEED_KEYS.includes(key) ? t(`studio.typeEditor.aspects.sheet.needs.${part}.${key}`) : key;

/** One need this attachment must settle (EAINT-897 C4, `GET /schemas/:id/needs`). */
export interface AttachmentNeedRow {
  /**
   * The need's identifier. The question it asks ("who reports it") and the noun
   * it wants ("perspective") are deterministic from it, so they are looked up
   * here through `t()` rather than arriving as prose from the service.
   */
  key: string;
  at: AttachmentNeedAt;
  /**
   * `undefined` = unanswered. The STORED answer — a perspective `name`, an arc
   * `templateSystemName`, a cadence token — never the display string, which differs by
   * locale. `options` carries the reading.
   */
  answer?: string;
  /** What this type actually has to offer. Empty means the answer cannot be given here. */
  options?: { value: string; label: string }[];
}

/**
 * One option the definition answered for itself and this type may answer differently.
 * `base`/`baseSay` are the definition's answer and how it reads in prose, so departing
 * from the author is never silent.
 */
export interface AttachmentOptionRow {
  key: string;
  label: string;
  control: 'seg' | 'select' | 'num';
  options?: { id: string; label: string }[];
  value: string;
  base: string;
  baseSay: string;
  hint?: string;
  /** Chasing options collapse behind a summary line until someone opens them. */
  group?: 'chase';
}

export interface AttachmentArc {
  name: string;
  stages: string[];
}

/** The attachment's own settings — the C6 per-attachment config store, not the definition. */
export interface AttachmentConfig {
  label?: string;
  startAs?: AttachmentStartState;
  weight?: string;
  arc?: string;
  wiring?: Record<string, Record<string, AttachmentStageRole>>;
  options?: Record<string, string>;
  answers?: Record<string, string>;
  answeredBy?: Record<string, AttachmentNeedAt>;
  targets?: (typeof TARGET_MODES)[number];
  pin?: (typeof PIN_MODES)[number];
  /**
   * The published version a `pin` holds this type on, as typed — the composer parses it
   * into the reference's `versionPin`. The registry does not say which version is
   * current, so pinning is a number the author states, not a lever that reads one.
   */
  pinnedVersion?: string;
  /** The attachment has been applied — reopening it clears this, never the settings. */
  confirmed?: boolean;
}

export interface AspectAttachmentSheetMoleculeProps {
  aspectName: string;
  typeName: string;
  /** The version this attachment would pin to, e.g. "v2". */
  version?: string;
  /** `null`/absent = the definition does not resolve in this workspace's registry. */
  definition?: AttachmentDefinition | null;
  config?: AttachmentConfig;
  needs?: AttachmentNeedRow[];
  options?: AttachmentOptionRow[];
  /** Field names the platform seals — named so refusing also says where to go. */
  sealedFields?: string[];
  arcs?: AttachmentArc[];
  /** The ATTACHMENT is evaluated (`row.evaluated`) — only then are targets a question. */
  evaluated?: boolean;
  /** The ancestor type that attached this aspect; absent for an own attachment. */
  inheritedFrom?: string;
  /** This type has overlaid settings on an inherited attachment. */
  reconfigured?: boolean;
  readOnly?: boolean;
  /** What is still unsettled, and whether the attachment has been applied. */
  review?: { open: string[]; confirmed: boolean };
  /**
   * Initial disclosure — the prototype keeps each of these in local state. They are
   * seams, not control: a caller (or a story) states the state the sheet opens in,
   * and the sheet owns it from there.
   */
  initialDefinitionOpen?: boolean;
  initialWiringOpen?: boolean;
  initialChaseOpen?: boolean;
  initialForking?: boolean;
  onChange?: (next: AttachmentConfig) => void;
  onClose?: () => void;
  /** Opens the aspect's full definition — absent when there is nowhere to open it. */
  onOpenDefinition?: () => void;
  onRevertAll?: () => void;
  onApply?: () => void;
  onRemove?: () => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * The sealed part of the attachment, readable without leaving the sheet — someone
 * configuring an attachment needs to see what they are configuring
 * (prototype `DefSummary`).
 */
const DefinitionSummary: React.FC<{
  definition: AttachmentDefinition;
  open: boolean;
  onToggle: () => void;
  onOpenDefinition?: () => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}> = ({ definition, open, onToggle, onOpenDefinition, t }) => {
  const expiry =
    definition.expiryDays === undefined
      ? t('studio.typeEditor.aspects.sheet.def.noExpiry')
      : t('studio.typeEditor.aspects.sheet.def.staleAfter', { days: definition.expiryDays });

  return (
    <div className="aspect-attachment-sheet__def" data-open={open ? '1' : undefined}>
      <div className="aspect-attachment-sheet__def-top">
        <i className="pi pi-lock" aria-hidden="true" />
        <span className="aspect-attachment-sheet__def-line">
          <b data-datum="compose.collected" data-kind="strong">
            {t(`studio.typeEditor.aspects.sheet.def.kind.${definition.kind}`)}
          </b>
          {definition.holds.length > 0 && (
            <>
              {' '}
              {t('studio.typeEditor.aspects.sheet.def.holdsPrefix')}{' '}
              {definition.holds.map((hold) => hold.name).join(', ')}
            </>
          )}{' '}
          {definition.evaluated
            ? t('studio.typeEditor.aspects.sheet.def.judgedAgainstTarget')
            : t('studio.typeEditor.aspects.sheet.def.watchedNotJudged')}
        </span>
        <ButtonAtom
          variant="text"
          size="small"
          icon={open ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
          label={
            open
              ? t('studio.typeEditor.aspects.sheet.def.less')
              : t('studio.typeEditor.aspects.sheet.def.whatItIs')
          }
          aria-expanded={open}
          onClick={onToggle}
          data-datum="compose.less"
          data-kind="button"
          data-testid="attachment-sheet-def-toggle"
        />
        {onOpenDefinition && (
          <ButtonAtom
            variant="text"
            size="small"
            icon="pi pi-external-link"
            label={t('studio.typeEditor.aspects.sheet.def.fullDefinition')}
            onClick={onOpenDefinition}
            data-datum="compose.full-definition"
            data-kind="button"
            data-testid="attachment-sheet-full-definition"
          />
        )}
      </div>
      {open && (
        <dl className="aspect-attachment-sheet__def-body" data-datum="compose.text#2" data-kind="text">
          <dt>{t('studio.typeEditor.aspects.sheet.def.holds')}</dt>
          <dd>
            {definition.holds.length ? (
              <div className="aspect-attachment-sheet__def-fields">
                {definition.holds.map((hold) => (
                  <div
                    key={hold.name}
                    className="aspect-attachment-sheet__def-field"
                    data-container="each(holds)"
                    title={hold.rules || undefined}
                    data-datum="compose.field"
                    data-kind="field"
                    data-layout="stacked"
                  >
                    <b data-datum="compose.h-name" data-kind="strong" data-layout="stacked">
                      {hold.name}
                    </b>
                    <span>{hold.type}</span>
                    <i>{t(`studio.typeEditor.aspects.sheet.def.fill.${hold.fill}`)}</i>
                  </div>
                ))}
              </div>
            ) : (
              <span>{t('studio.typeEditor.aspects.sheet.def.holdsNothing')}</span>
            )}
          </dd>
          <dt>{t('studio.typeEditor.aspects.sheet.def.judged')}</dt>
          <dd>
            <span>
              {definition.bands.length
                ? definition.bands
                    .map((band) =>
                      t('studio.typeEditor.aspects.sheet.def.bandAt', {
                        status: band.status.replaceAll('_', ' '),
                        pct: band.pct,
                      })
                    )
                    .join(' · ')
                : t('studio.typeEditor.aspects.sheet.def.notJudged')}
            </span>
          </dd>
          <dt>{t('studio.typeEditor.aspects.sheet.def.truth')}</dt>
          <dd>
            <span>
              {t(`studio.typeEditor.aspects.sheet.def.temporal.${definition.temporal}`)}
              {expiry}
            </span>
          </dd>
          <dt>{t('studio.typeEditor.aspects.sheet.def.shared')}</dt>
          <dd>
            <span>{t('studio.typeEditor.aspects.sheet.def.sharedNote')}</span>
          </dd>
        </dl>
      )}
    </div>
  );
};

/** `CSection` — a labelled group with an optional note (prototype `aspect-collection.jsx:88`). */
const Section: React.FC<{
  label: string;
  labelDatum: string;
  note?: string;
  children?: React.ReactNode;
}> = ({ label, labelDatum, note, children }) => (
  <div className="aspect-attachment-sheet__section">
    <div className="aspect-attachment-sheet__section-head">
      <span data-datum={labelDatum} data-kind="heading">
        {label}
      </span>
      {note && <i>{note}</i>}
    </div>
    {children}
  </div>
);

const AspectAttachmentSheetMolecule: React.FC<AspectAttachmentSheetMoleculeProps> = ({
  aspectName,
  typeName,
  version,
  definition,
  config = {},
  needs = [],
  options = [],
  sealedFields = [],
  arcs = [],
  evaluated = false,
  inheritedFrom,
  reconfigured = false,
  readOnly = false,
  review,
  initialDefinitionOpen = false,
  initialWiringOpen,
  initialChaseOpen = false,
  initialForking = false,
  onChange,
  onClose,
  onOpenDefinition,
  onRevertAll,
  onApply,
  onRemove,
  className = '',
  'data-testid': dataTestId = 'aspect-attachment-sheet',
}) => {
  const { t } = useTranslation();
  const idBase = useId();

  const wiring = config.wiring ?? {};
  const wiredArcs = Object.entries(wiring)
    .map(
      ([arcKey, stageRoles]) =>
        [arcKey, Object.entries(stageRoles ?? {}).filter(([, role]) => role && role !== 'none')] as const
    )
    .filter(([, stageRoles]) => stageRoles.length > 0);

  const [definitionOpen, setDefinitionOpen] = useState(initialDefinitionOpen);
  const [wiringOpen, setWiringOpen] = useState(initialWiringOpen ?? wiredArcs.length > 0);
  const [chaseOpen, setChaseOpen] = useState(initialChaseOpen);
  const [forking, setForking] = useState(initialForking);

  const set = (patch: Partial<AttachmentConfig>) => onChange?.({ ...config, ...patch });

  const arcName = config.arc ?? arcs[0]?.name;
  const arc = arcs.find((candidate) => candidate.name === arcName) ?? arcs[0];
  const stages = arc?.stages ?? [];
  const arcWiring = (arcName ? wiring[arcName] : undefined) ?? {};
  const setStage = (stage: string, role: AttachmentStageRole) => {
    if (!arcName) return;
    set({ wiring: { ...wiring, [arcName]: { ...arcWiring, [stage]: role } } });
  };
  // A role that raises a check-in cannot exist for a value nobody is asked for.
  const roles = STAGE_ROLES.filter((role) => !(role.asks && definition?.computed));

  const mainOptions = options.filter((option) => option.group !== 'chase');
  const chaseOptions = options.filter((option) => option.group === 'chase');
  const overridden = options.filter((option) => option.value !== option.base);

  // Only a need this type owns can block it — one handed to each sphere is answered
  // by the sphere, at creation, which always happens.
  const openNeeds = needs.filter((need) => need.at === 'type' && !need.answer);

  const startAs = config.startAs ?? 'active';
  const targets = config.targets ?? 'defer';
  const pin = config.pin ?? 'float';
  // What the pin option is called: the version it holds when one is known (`version`),
  // otherwise the act itself — "Pin to v1" for a floating attachment would name a version
  // nobody chose.
  // The typed version is held here until blur or Enter. Every `set()` is a write the
  // composer persists, re-reads and reports, so committing per keystroke would send `1`
  // then `12` as two edits and race the second against the first's re-read.
  const [pinnedDraft, setPinnedDraft] = useState(config.pinnedVersion ?? '');
  // Whether the field has been left since it last matched the config (ux-craft F3): `0` is
  // the wrong answer and a keystroke on the way to `10`, so the message waits for blur or
  // Enter — and goes the moment the value is right.
  const [pinnedDraftTouched, setPinnedDraftTouched] = useState(false);
  useEffect(() => {
    setPinnedDraft(config.pinnedVersion ?? '');
    setPinnedDraftTouched(false);
  }, [config.pinnedVersion]);
  // The same test the composer applies before it pins: a whole number, 1 or more.
  const pinnedDraftMalformed = pinnedDraft.trim() !== '' && !/^[1-9]\d*$/.test(pinnedDraft.trim());
  const pinnedDraftInvalid = pinnedDraftTouched && pinnedDraftMalformed;
  const commitPinnedDraft = () => {
    setPinnedDraftTouched(true);
    if (pinnedDraftMalformed) return;
    if (pinnedDraft !== (config.pinnedVersion ?? '')) set({ pinnedVersion: pinnedDraft });
  };

  const setAnsweredBy = (need: AttachmentNeedRow, at: AttachmentNeedAt) => {
    const answers = { ...(config.answers ?? {}) };
    // Who answers and what they answered are one decision — handing the need to the
    // sphere must not leave this type's stale pick sitting underneath it.
    if (at !== 'type') delete answers[need.key];
    set({ answeredBy: { ...(config.answeredBy ?? {}), [need.key]: at }, answers });
  };

  const wiringSay = wiredArcs
    .map(
      ([name, stageRoles]) =>
        stageRoles
          .map(([stage, role]) =>
            t('studio.typeEditor.aspects.sheet.wiring.roleIn', {
              role: t(`studio.typeEditor.aspects.sheet.wiring.role.${role}`),
              stage,
            })
          )
          .join(' · ') + (wiredArcs.length > 1 ? ` (${name})` : '')
    )
    .join(' — ');

  const optionRow = (option: AttachmentOptionRow) => {
    const changed = option.value !== option.base;
    return (
      <div className="aspect-attachment-sheet__row" key={option.key}>
        <span className="aspect-attachment-sheet__label">{option.label}</span>
        {option.control === 'num' ? (
          <span className="aspect-attachment-sheet__num">
            <InputAtom
              value={option.value}
              disabled={readOnly}
              aria-label={option.label}
              onChange={(event) =>
                set({ options: { ...(config.options ?? {}), [option.key]: event.target.value } })
              }
            />
          </span>
        ) : option.control === 'select' ? (
          <span>
            <DropdownAtom
              value={option.value}
              options={(option.options ?? []).map((entry) => ({ label: entry.label, value: entry.id }))}
              optionLabel="label"
              optionValue="value"
              disabled={readOnly}
              ariaLabel={option.label}
              onChange={(event) => set({ options: { ...(config.options ?? {}), [option.key]: event.value } })}
            />
          </span>
        ) : (
          <SegmentedControlAtom
            mode="single"
            size="small"
            ariaLabel={option.label}
            value={option.value}
            options={(option.options ?? []).map((entry) => ({
              value: entry.id,
              label: entry.label,
              disabled: readOnly,
            }))}
            onChange={(next) => set({ options: { ...(config.options ?? {}), [option.key]: next as string } })}
          />
        )}
        {changed ? (
          <span className="aspect-attachment-sheet__override">
            <i className="pi pi-pencil" aria-hidden="true" />
            {t('studio.typeEditor.aspects.sheet.options.definitionSays', { say: option.baseSay })}
            {!readOnly && (
              <ButtonAtom
                variant="text"
                size="small"
                label={t('studio.typeEditor.aspects.sheet.options.revert')}
                onClick={() => {
                  const next = { ...(config.options ?? {}) };
                  delete next[option.key];
                  set({ options: next });
                }}
              />
            )}
          </span>
        ) : (
          <span className="aspect-attachment-sheet__hint">
            {option.hint ?? t('studio.typeEditor.aspects.sheet.options.asDefinitionSet')}
          </span>
        )}
      </div>
    );
  };

  const sheetRef = useRef<HTMLDivElement>(null);
  // An expanded body inside the list, not a modal: focus stays where the reader put
  // it and Tab runs on past. Escape closes the sheet when there is a way to close it.
  useDialogFocus(true, sheetRef, { onDismiss: onClose, trapFocus: false, manageFocus: false });

  const rootClasses = ['aspect-attachment-sheet', className].filter(Boolean).join(' ');

  return (
    <div className={rootClasses} data-testid={dataTestId} ref={sheetRef}>
      {inheritedFrom && (
        <div className="aspect-attachment-sheet__inherit">
          <i className="pi pi-sitemap" aria-hidden="true" />
          <span className="aspect-attachment-sheet__inherit-text">
            <b data-datum="compose.attached-this" data-kind="strong">
              {t('studio.typeEditor.aspects.sheet.inherit.attachedThis', { schema: inheritedFrom })}
            </b>{' '}
            {t('studio.typeEditor.aspects.sheet.inherit.definitionStaysTheirs', { typeName })}
          </span>
          {reconfigured && !readOnly && (
            <ButtonAtom
              variant="text"
              size="small"
              icon="pi pi-replay"
              label={t('studio.typeEditor.aspects.sheet.inherit.followAgain', { schema: inheritedFrom })}
              onClick={onRevertAll}
              data-datum="compose.follow"
              data-kind="button"
              data-testid="attachment-sheet-revert-all"
            />
          )}
        </div>
      )}

      <div className="aspect-attachment-sheet__head" data-datum="compose.on-this-type" data-kind="heading">
        <i className="pi pi-sliders-h" aria-hidden="true" />
        <b data-datum="compose.on-this-type#2" data-kind="strong">
          {t('studio.typeEditor.aspects.sheet.head.title', { name: aspectName })}
        </b>
        <span
          className="aspect-attachment-sheet__hint"
          data-datum="compose.the-definition-is-sealed-these-are-this-"
          data-kind="hint"
        >
          {t('studio.typeEditor.aspects.sheet.head.sealedHint')}
        </span>
        <ButtonAtom
          variant="text"
          size="small"
          label={t('studio.typeEditor.aspects.sheet.head.done')}
          onClick={onClose}
          data-datum="compose.done"
          data-kind="button"
          data-testid="attachment-sheet-done"
        />
      </div>

      <div className="aspect-attachment-sheet__body" data-datum="compose.text" data-kind="text">
        {definition && (
          <DefinitionSummary
            definition={definition}
            open={definitionOpen}
            onToggle={() => setDefinitionOpen((value) => !value)}
            onOpenDefinition={onOpenDefinition}
            t={t}
          />
        )}

        {/* Three separate decisions, so they read as three rows rather than one
            grab-bag: what it is called here, whether it is live, how much it counts. */}
        <Section label={t('studio.typeEditor.aspects.sheet.onThisType.label')} labelDatum="compose.label">
          <div className="aspect-attachment-sheet__row">
            <span className="aspect-attachment-sheet__label" data-datum="compose.called" data-kind="label">
              {t('studio.typeEditor.aspects.sheet.onThisType.called')}
            </span>
            <InputAtom
              value={config.label ?? ''}
              placeholder={aspectName}
              readOnly={readOnly}
              aria-label={t('studio.typeEditor.aspects.sheet.onThisType.called')}
              onChange={(event) => set({ label: event.target.value })}
              data-datum="compose.input"
              data-kind="input"
              data-testid="attachment-sheet-label"
            />
            <span
              className="aspect-attachment-sheet__hint"
              data-datum="compose.overrides-the-aspect-s-own-name-on-spher"
              data-kind="hint"
            >
              {t('studio.typeEditor.aspects.sheet.onThisType.calledHint')}
            </span>
          </div>
          <div className="aspect-attachment-sheet__row">
            <span className="aspect-attachment-sheet__label" data-datum="compose.state" data-kind="label">
              {t('studio.typeEditor.aspects.sheet.onThisType.state')}
            </span>
            <SegmentedControlAtom
              mode="single"
              size="small"
              ariaLabel={t('studio.typeEditor.aspects.sheet.onThisType.state')}
              value={startAs}
              optionKind="button"
              renderAs="button"
              options={START_STATES.map((state) => ({
                value: state,
                label: t(`studio.typeEditor.aspects.sheet.onThisType.startState.${state}`),
                disabled: readOnly,
                datum: state === startAs ? 'compose.o-label' : undefined,
              }))}
              onChange={(next) => set({ startAs: next as AttachmentStartState })}
              data-testid="attachment-sheet-start-state"
            />
            <span
              className="aspect-attachment-sheet__hint"
              data-datum="compose.attach-states-find-s-s-0-v-startas-activ"
              data-kind="hint"
            >
              {t(`studio.typeEditor.aspects.sheet.onThisType.startStateNote.${startAs}`)}
            </span>
          </div>
          <div className="aspect-attachment-sheet__row">
            <span className="aspect-attachment-sheet__label" data-datum="compose.weight" data-kind="label">
              {t('studio.typeEditor.aspects.sheet.onThisType.weight')}
            </span>
            <span className="aspect-attachment-sheet__num" data-datum="compose.input#2" data-kind="input">
              <InputAtom
                value={config.weight ?? '1'}
                disabled={readOnly}
                aria-label={t('studio.typeEditor.aspects.sheet.onThisType.weight')}
                onChange={(event) => set({ weight: event.target.value })}
                data-datum="compose.input#3"
                data-kind="input"
                data-testid="attachment-sheet-weight"
              />
            </span>
            <span
              className="aspect-attachment-sheet__hint"
              data-datum="compose.how-much-it-moves-this-type-s-health-aga"
              data-kind="hint"
            >
              {t('studio.typeEditor.aspects.sheet.onThisType.weightHint')}
            </span>
          </div>
        </Section>

        {/* Needs, answered at this site. The label is the question being answered, and
            the options are what this type actually has. A kind with nothing to offer says
            so — that is the answer, and it is a reason not to attach here. */}
        <Section
          label={t('studio.typeEditor.aspects.sheet.needs.label')}
          labelDatum="compose.label#2"
          note={t('studio.typeEditor.aspects.sheet.needs.note')}
        >
          {needs.length > 0 ? (
            needs.map((need) => {
              const answered = !!need.answer;
              const needOptions = need.options ?? [];
              const ask = needCopy(need.key, 'ask', t);
              return (
                <div className="aspect-attachment-sheet__need" key={need.key} data-container="each(needs)">
                  <div className="aspect-attachment-sheet__row">
                    <span className="aspect-attachment-sheet__label" data-datum="compose.n-ask" data-kind="label">
                      {ask}
                    </span>
                    <SegmentedControlAtom
                      mode="single"
                      size="small"
                      ariaLabel={ask}
                      value={need.at}
                      optionKind="button"
              renderAs="button"
                      options={NEED_ANSWERERS.map((at) => ({
                        value: at,
                        label: t(`studio.typeEditor.aspects.sheet.needs.answerer.${at}`),
                        disabled: readOnly,
                        datum: at === need.at ? 'compose.o-label#2' : undefined,
                      }))}
                      onChange={(next) => setAnsweredBy(need, next as AttachmentNeedAt)}
                      data-testid={`attachment-sheet-need-at-${need.key}`}
                    />
                    {need.at === 'type' && !answered && (
                      <span className="aspect-attachment-sheet__chip" data-datum="compose.to-set" data-kind="chip">
                        {t('studio.typeEditor.aspects.sheet.needs.toSet')}
                      </span>
                    )}
                    <span
                      className="aspect-attachment-sheet__hint"
                      data-datum="compose.answerers-find-a-a-id-n-at-note"
                      data-kind="hint"
                    >
                      {t(`studio.typeEditor.aspects.sheet.needs.answererNote.${need.at}`)}
                    </span>
                  </div>
                  {need.at === 'type' && (
                    <div className="aspect-attachment-sheet__row">
                      <span
                        className="aspect-attachment-sheet__label"
                        data-datum="compose.label#3"
                        data-kind="label"
                      />
                      {needOptions.length > 0 ? (
                        <span data-datum="compose.select" data-kind="select">
                          <DropdownAtom
                            // The selection heads the list, then everything else in the type's
                            // own order. An answer whose option has since disappeared still
                            // heads it, labelled by what is on the record, so the row never
                            // renders blank for a value that is really there.
                            value={need.answer ?? ''}
                            options={(answered
                              ? [
                                  needOptions.find((option) => option.value === need.answer) ?? {
                                    value: need.answer as string,
                                    label: need.answer as string,
                                  },
                                ]
                              : [{ value: '', label: t('studio.typeEditor.aspects.sheet.needs.notAnswered') }]
                            ).concat(needOptions.filter((option) => option.value !== need.answer))}
                            optionLabel="label"
                            optionValue="value"
                            disabled={readOnly}
                            ariaLabel={ask}
                            onChange={(event) =>
                              set({ answers: { ...(config.answers ?? {}), [need.key]: event.value } })
                            }
                            data-testid={`attachment-sheet-need-answer-${need.key}`}
                          />
                        </span>
                      ) : (
                        <span className="aspect-attachment-sheet__none">
                          <i className="pi pi-ban" aria-hidden="true" />
                          {t('studio.typeEditor.aspects.sheet.needs.noneExists', {
                            noun: needCopy(need.key, 'noun', t),
                          })}
                        </span>
                      )}
                      {need.key === 'cadence' && definition?.temporal === 'effective_interval' && (
                        <span
                          className="aspect-attachment-sheet__hint"
                          data-datum="compose.calendars-only"
                          data-kind="hint"
                        >
                          {t('studio.typeEditor.aspects.sheet.needs.calendarsOnly', { name: aspectName })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : definition ? (
            <p
              className="aspect-attachment-sheet__note"
              data-datum="compose.this-aspect-declares-no-needs-nothing-to"
              data-kind="note"
            >
              {t('studio.typeEditor.aspects.sheet.needs.declaresNone')}
            </p>
          ) : (
            /* "No needs" is a claim about the definition. With no definition to read we
               cannot make it, and saying it anyway is how an attachment reports itself
               settled when nobody has been assigned to it. */
            <p
              className="aspect-attachment-sheet__note"
              data-datum="compose.s-definition-isn-t-in-this-workspace-s-r"
              data-kind="note"
            >
              {t('studio.typeEditor.aspects.sheet.needs.unresolvedDefinition', { name: aspectName })}
            </p>
          )}
          {needs.length > 0 && (
            <p className="aspect-attachment-sheet__note" data-datum="compose.approachnote" data-kind="note">
              {t('studio.typeEditor.aspects.sheet.needs.approachNote', { typeName })}
            </p>
          )}
          {definition?.kind === 'manual' && definition.adhoc && (
            <p className="aspect-attachment-sheet__note" data-datum="compose.no-cadence-to-answer" data-kind="note">
              {t('studio.typeEditor.aspects.sheet.needs.noCadence', { name: aspectName })}
            </p>
          )}
          {openNeeds.length > 0 && (
            <p className="aspect-attachment-sheet__note" data-datum="compose.until" data-kind="note">
              {t('studio.typeEditor.aspects.sheet.needs.blocked', {
                count: openNeeds.length,
                name: aspectName,
              })}
            </p>
          )}
        </Section>

        {/* Options — sealed is what the definition decides and nobody may change; needs
            are what it could not decide; these are what it decided reasonably, for
            itself, without binding this type. */}
        {options.length > 0 && (
          <Section
            label={t('studio.typeEditor.aspects.sheet.options.label')}
            labelDatum="compose.label#4"
            note={
              overridden.length > 0
                ? t('studio.typeEditor.aspects.sheet.options.changedNote', { count: overridden.length })
                : t('studio.typeEditor.aspects.sheet.options.note')
            }
          >
            {mainOptions.map(optionRow)}

            {chaseOptions.length > 0 &&
              (chaseOpen ? (
                <>
                  {chaseOptions.map(optionRow)}
                  {!readOnly && (
                    <ButtonAtom
                      variant="text"
                      size="small"
                      icon="pi pi-chevron-up"
                      label={t('studio.typeEditor.aspects.sheet.options.collapseChasing')}
                      onClick={() => setChaseOpen(false)}
                      data-datum="compose.collapse-chasing"
                      data-kind="button"
                      data-testid="attachment-sheet-chase-collapse"
                    />
                  )}
                </>
              ) : (
                <div className="aspect-attachment-sheet__optsum">
                  <i className="pi pi-clock" aria-hidden="true" />
                  <span>
                    {chaseOptions.map((option) => `${option.label.toLowerCase()} ${option.baseSay}`).join(' · ')}
                  </span>
                  {!readOnly && (
                    <ButtonAtom
                      variant="text"
                      size="small"
                      label={t('studio.typeEditor.aspects.sheet.options.change')}
                      onClick={() => setChaseOpen(true)}
                      data-datum="compose.change"
                      data-kind="button"
                      data-testid="attachment-sheet-chase-change"
                    />
                  )}
                </div>
              ))}

            {/* Refusing without offering the legitimate path is how you get five hand-made
                copies of an aspect. Sealed says no and says where to go. */}
            <div className="aspect-attachment-sheet__sealed">
              <i className="pi pi-lock" aria-hidden="true" />
              <span className="aspect-attachment-sheet__sealed-text">
                <b data-datum="side.sealed-by-the-platform" data-kind="strong">
                  {t('studio.typeEditor.aspects.sheet.options.sealedTitle')}
                </b>{' '}
                {t('studio.typeEditor.aspects.sheet.options.sealedBody', {
                  fields: sealedFields.join(', '),
                  name: aspectName,
                  typeName,
                })}
              </span>
              {!readOnly && (
                <ButtonAtom
                  variant="text"
                  size="small"
                  label={t('studio.typeEditor.aspects.sheet.options.needOneDifferent')}
                  onClick={() => setForking(true)}
                  data-datum="side.need-one-different"
                  data-kind="button"
                  data-testid="attachment-sheet-fork"
                />
              )}
            </div>

            {forking && (
              <div className="aspect-attachment-sheet__fork" data-datum="side.callout" data-kind="callout">
                <div className="aspect-attachment-sheet__fork-top">
                  <i className="pi pi-sitemap" aria-hidden="true" />
                  <b data-datum="side.fork" data-kind="strong">
                    {t('studio.typeEditor.aspects.sheet.options.forkTitle', { name: aspectName, typeName })}
                  </b>
                </div>
                <p
                  className="aspect-attachment-sheet__hint"
                  data-datum="side.a-sealed-field-can-t-be-overridden-but-i"
                  data-kind="hint"
                >
                  {t('studio.typeEditor.aspects.sheet.options.forkBody', { name: aspectName })}
                </p>
                <div className="aspect-attachment-sheet__fork-actions">
                  <ButtonAtom
                    variant="primary"
                    size="small"
                    label={t('studio.typeEditor.aspects.sheet.options.forkIt')}
                    onClick={() => setForking(false)}
                    data-datum="side.fork-it"
                    data-kind="button"
                  />
                  <ButtonAtom
                    variant="text"
                    size="small"
                    label={t('common.cancel')}
                    onClick={() => setForking(false)}
                    data-datum="side.cancel#2"
                    data-kind="button"
                  />
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Arc wiring — the reason this screen is more than the aspect composer. Closed
            until it carries something: an unopened section must not look like a set of
            decisions that were taken. */}
        <Section
          label={t('studio.typeEditor.aspects.sheet.wiring.label')}
          labelDatum="compose.label#5"
          note={
            arcs.length === 0
              ? t('studio.typeEditor.aspects.sheet.wiring.noArcsNote')
              : definition?.computed
              ? t('studio.typeEditor.aspects.sheet.wiring.computedNote')
              : t('studio.typeEditor.aspects.sheet.wiring.note', {
                  arcs: arcs.map((entry) => entry.name).join(' and '),
                })
          }
        >
          {arcs.length === 0 ? (
            <p
              className="aspect-attachment-sheet__note"
              data-datum="compose.this-type-runs-no-arcs-so-there-are-no-s"
              data-kind="note"
            >
              {t('studio.typeEditor.aspects.sheet.wiring.noArcs')}
            </p>
          ) : !wiringOpen ? (
            <div
              className="aspect-attachment-sheet__wiresum"
              data-on={wiredArcs.length ? '1' : undefined}
              data-datum="compose.is-collected-on-its-own-terms-no-stage-a"
              data-kind="text"
            >
              <i className={wiredArcs.length ? 'pi pi-share-alt' : 'pi pi-minus'} aria-hidden="true" />
              <span
                className="aspect-attachment-sheet__wiresum-text"
                data-datum="compose.is-collected-on-its-own-terms-no-stage-a#2"
                data-kind="label"
              >
                {wiredArcs.length ? (
                  wiringSay
                ) : (
                  <>
                    <b data-datum="compose.not-wired-into-any-stage" data-kind="strong">
                      {t('studio.typeEditor.aspects.sheet.wiring.notWired')}
                    </b>{' '}
                    {t('studio.typeEditor.aspects.sheet.wiring.notWiredBody', { name: aspectName })}
                  </>
                )}
              </span>
              {!readOnly && (
                <ButtonAtom
                  variant="text"
                  size="small"
                  label={
                    wiredArcs.length
                      ? t('studio.typeEditor.aspects.sheet.wiring.change')
                      : t('studio.typeEditor.aspects.sheet.wiring.wireIt')
                  }
                  onClick={() => setWiringOpen(true)}
                  data-datum="compose.change#2"
                  data-kind="button"
                  data-testid="attachment-sheet-wiring-open"
                />
              )}
            </div>
          ) : (
            <>
              <div className="aspect-attachment-sheet__row">
                <span className="aspect-attachment-sheet__label" data-datum="compose.arc" data-kind="label">
                  {t('studio.typeEditor.aspects.sheet.wiring.arc')}
                </span>
                <span data-datum="compose.select#2" data-kind="select">
                  <DropdownAtom
                    value={arcName}
                    options={arcs.map((entry) => ({ label: entry.name, value: entry.name }))}
                    optionLabel="label"
                    optionValue="value"
                    disabled={readOnly}
                    ariaLabel={t('studio.typeEditor.aspects.sheet.wiring.arc')}
                    onChange={(event) => set({ arc: event.value })}
                    data-testid="attachment-sheet-wiring-arc"
                  />
                </span>
                <span className="aspect-attachment-sheet__hint" data-datum="compose.stage" data-kind="hint">
                  {t('studio.typeEditor.aspects.sheet.wiring.stageCount', { count: stages.length })}
                </span>
              </div>
              {stages.length > 0 ? (
                <div className="aspect-attachment-sheet__stages">
                  {stages.map((stage) => {
                    const role = arcWiring[stage] ?? 'none';
                    return (
                      <div
                        key={stage}
                        className="aspect-attachment-sheet__stage"
                        data-container="each(stages)"
                        data-on={role !== 'none' ? '1' : undefined}
                      >
                        <span className="aspect-attachment-sheet__stage-name">{stage}</span>
                        <div className="aspect-attachment-sheet__stage-roles" role="group" aria-label={stage}>
                          {roles.map((entry) => (
                            <ButtonAtom
                              key={entry.id}
                              data-container="each(stages) > each(roles)"
                              variant={role === entry.id ? 'secondary' : 'text'}
                              size="small"
                              label={t(`studio.typeEditor.aspects.sheet.wiring.role.${entry.id}`)}
                              title={t(`studio.typeEditor.aspects.sheet.wiring.roleNote.${entry.id}`)}
                              disabled={readOnly}
                              onClick={() => setStage(stage, entry.id)}
                            />
                          ))}
                        </div>
                        <span className="aspect-attachment-sheet__note" data-datum="compose.meta-note" data-kind="note">
                          {t(`studio.typeEditor.aspects.sheet.wiring.roleNote.${role}`)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p
                  className="aspect-attachment-sheet__note"
                  data-datum="compose.has-no-stages-defined-yet"
                  data-kind="note"
                >
                  {t('studio.typeEditor.aspects.sheet.wiring.noStages', { arc: arcName })}
                </p>
              )}
              <p
                className="aspect-attachment-sheet__note"
                data-datum="compose.is-computed-not-asked-so-no-stage-can-ra"
                data-kind="note"
              >
                {definition?.computed
                  ? t('studio.typeEditor.aspects.sheet.wiring.computedGate', { name: aspectName })
                  : t('studio.typeEditor.aspects.sheet.wiring.requiredGate')}
              </p>
              {!readOnly && (
                <ButtonAtom
                  variant="text"
                  size="small"
                  icon="pi pi-chevron-up"
                  label={
                    wiredArcs.length
                      ? t('studio.typeEditor.aspects.sheet.wiring.collapse')
                      : t('studio.typeEditor.aspects.sheet.wiring.leaveUnwired')
                  }
                  onClick={() => setWiringOpen(false)}
                  data-datum="compose.collapse"
                  data-kind="button"
                  data-testid="attachment-sheet-wiring-collapse"
                />
              )}
            </>
          )}
        </Section>

        <Section
          label={t('studio.typeEditor.aspects.sheet.targets.label')}
          labelDatum="compose.label#6"
          note={t('studio.typeEditor.aspects.sheet.targets.note')}
        >
          {evaluated ? (
            <>
              <SegmentedControlAtom
                mode="single"
                size="small"
                ariaLabel={t('studio.typeEditor.aspects.sheet.targets.label')}
                value={targets}
                optionKind="button"
              renderAs="button"
                options={TARGET_MODES.map((mode) => ({
                  value: mode,
                  label: t(`studio.typeEditor.aspects.sheet.targets.mode.${mode}`),
                  disabled: readOnly,
                  datum: mode === targets ? 'compose.o-label#3' : undefined,
                }))}
                onChange={(next) => set({ targets: next as AttachmentConfig['targets'] })}
                data-testid="attachment-sheet-targets"
              />
              <p
                className="aspect-attachment-sheet__note"
                data-datum="compose.you-ll-enter-q3-and-q4-before-publishing"
                data-kind="note"
              >
                {t(`studio.typeEditor.aspects.sheet.targets.modeNote.${targets}`)}
              </p>
            </>
          ) : (
            <p
              className="aspect-attachment-sheet__note"
              data-datum="compose.isn-t-evaluated-against-a-target-so-ther"
              data-kind="note"
            >
              {t('studio.typeEditor.aspects.sheet.targets.notEvaluated', { name: aspectName })}
            </p>
          )}
        </Section>

        <Section label={t('studio.typeEditor.aspects.sheet.version.label')} labelDatum="compose.label#7">
          <div className="aspect-attachment-sheet__row">
            <SegmentedControlAtom
              mode="single"
              size="small"
              ariaLabel={t('studio.typeEditor.aspects.sheet.version.label')}
              value={pin}
              optionKind="button"
              renderAs="button"
              options={PIN_MODES.map((mode) => ({
                value: mode,
                label:
                  mode === 'float'
                    ? t('studio.typeEditor.aspects.sheet.version.follow')
                    : version
                      ? t('studio.typeEditor.aspects.sheet.version.pinTo', { version: version })
                      : t('studio.typeEditor.aspects.sheet.version.pinToAVersion'),
                disabled: readOnly,
                datum: mode === pin ? 'compose.o-label#4' : undefined,
              }))}
              onChange={(next) => set({ pin: next as AttachmentConfig['pin'] })}
              data-testid="attachment-sheet-pin"
            />
            <span
              className="aspect-attachment-sheet__hint"
              data-datum="compose.new-published-versions-arrive-automatica"
              data-kind="hint"
            >
              {pin === 'float'
                ? t('studio.typeEditor.aspects.sheet.version.floatHint')
                : version
                  ? t('studio.typeEditor.aspects.sheet.version.pinHint', { version: version })
                  : t('studio.typeEditor.aspects.sheet.version.pinNeedsVersion')}
            </span>
          </div>
          {/*
            The number the pin holds, asked here with the lever that needs it rather than as a
            bare input on the collapsed row (`type-composer.jsx:1224` renders the version as
            text; only the sheet edits it). Labelled, because a placeholder is not a label.
          */}
          {pin === 'pin' && (
            <div className="aspect-attachment-sheet__row">
              <label
                className="aspect-attachment-sheet__label"
                htmlFor={`${idBase}-pinned-version`}
              >
                {t('studio.typeEditor.aspects.sheet.version.pinnedVersionLabel')}
              </label>
              <span className="aspect-attachment-sheet__num">
                <InputAtom
                  id={`${idBase}-pinned-version`}
                  type="number"
                  min={1}
                  step={1}
                  value={pinnedDraft}
                  disabled={readOnly}
                  inputMode="numeric"
                  aria-invalid={pinnedDraftInvalid || undefined}
                  aria-describedby={pinnedDraftInvalid ? `${idBase}-pinned-version-error` : undefined}
                  onChange={(event) => setPinnedDraft(event.target.value)}
                  onBlur={commitPinnedDraft}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitPinnedDraft();
                    }
                  }}
                  data-testid="attachment-sheet-pinned-version"
                />
              </span>
              {pinnedDraftInvalid && (
                <span
                  id={`${idBase}-pinned-version-error`}
                  className="aspect-attachment-sheet__error"
                  role="alert"
                  data-testid="attachment-sheet-pinned-version-error"
                >
                  {t('studio.typeEditor.aspects.sheet.version.pinInvalid')}
                </span>
              )}
            </div>
          )}
        </Section>

        {/* One footer: what is left, then the two acts — apply, or remove. The open items
            are named so applying is never a guess. */}
        <div
          className="aspect-attachment-sheet__foot"
          data-state={
            review && !readOnly ? (review.open.length ? 'open' : review.confirmed ? 'ok' : 'ready') : undefined
          }
        >
          {!readOnly && (
            <ButtonAtom
              variant="danger"
              size="small"
              label={
                inheritedFrom
                  ? t('studio.typeEditor.aspects.sheet.foot.cantDetach')
                  : t('studio.typeEditor.aspects.sheet.foot.detach')
              }
              title={
                inheritedFrom
                  ? t('studio.typeEditor.aspects.sheet.foot.cantDetachReason', { schema: inheritedFrom })
                  : t('studio.typeEditor.aspects.sheet.foot.detachReason', { name: aspectName })
              }
              onClick={onRemove}
              data-datum="compose.can-t-detach"
              data-kind="button"
              data-testid="attachment-sheet-detach"
            />
          )}
          {!readOnly && review && (
            <span className="aspect-attachment-sheet__state">
              {review.open.length > 0 ? (
                <>
                  <b data-datum="compose.left-to-set" data-kind="strong">
                    {t('studio.typeEditor.aspects.sheet.foot.leftToSet', { count: review.open.length })}
                  </b>
                  <span>{review.open.join(', ')}</span>
                </>
              ) : review.confirmed ? (
                <>
                  <i className="pi pi-check" aria-hidden="true" />
                  <b data-datum="compose.applied" data-kind="strong">
                    {t('studio.typeEditor.aspects.sheet.foot.applied')}
                  </b>
                  <span>{t('studio.typeEditor.aspects.sheet.foot.appliedNote')}</span>
                </>
              ) : (
                <>
                  <b data-datum="compose.nothing-left-open" data-kind="strong">
                    {t('studio.typeEditor.aspects.sheet.foot.nothingLeftOpen')}
                  </b>
                  <span>{t('studio.typeEditor.aspects.sheet.foot.confirmToFinish')}</span>
                </>
              )}
            </span>
          )}
          {!readOnly && review && !review.confirmed && (
            <ButtonAtom
              variant="primary"
              size="small"
              label={t('studio.typeEditor.aspects.sheet.foot.apply')}
              disabled={review.open.length > 0}
              title={review.open.length > 0 ? t('studio.typeEditor.aspects.sheet.foot.applyBlocked') : undefined}
              onClick={onApply}
              data-datum="compose.apply"
              data-kind="button"
              data-testid="attachment-sheet-apply"
            />
          )}
          {!readOnly && review && review.confirmed && (
            <ButtonAtom
              variant="text"
              size="small"
              label={t('studio.typeEditor.aspects.sheet.foot.reopen')}
              onClick={() => set({ confirmed: false })}
              data-datum="compose.reopen"
              data-kind="button"
              data-testid="attachment-sheet-reopen"
            />
          )}
          {(readOnly || !review) && (
            <span
              className="aspect-attachment-sheet__hint"
              data-datum="compose.removing-keeps-the-definition-only-this-"
              data-kind="hint"
            >
              {t('studio.typeEditor.aspects.sheet.foot.removingKeeps')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

AspectAttachmentSheetMolecule.displayName = 'AspectAttachmentSheetMolecule';

export default AspectAttachmentSheetMolecule;
